import {
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { Money } from '../../models/workbench-row.model'
import { RowOrigin, TARGET_META } from '../../models/source-key.model'

/**
 * Currency symbols for the money formatter. Covers the currencies present in the seed data
 * (Clearwater cash positions and Enfusion cash balances); anything unmapped falls back to its ISO
 * code so an unexpected currency is still legible.
 */
const CCY_SYMBOL: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CHF: 'CHF ',
  CAD: 'C$',
  AUD: 'A$',
  KRW: '₩',
  TWD: 'NT$',
  NOK: 'kr ',
  PLN: 'zł ',
}

const MONEY_DECIMALS = 2

/** Placeholder for an absent value — a dimmed em dash reads as "nothing here", not "zero". */
const EMPTY = '—'

export const isMoney = (value: unknown): value is Money =>
  typeof value === 'object' &&
  value !== null &&
  'value' in value &&
  'currency' in value &&
  typeof (value as Money).value === 'number'

/** Per-currency formatting: symbol, 2dp, negatives in parentheses. */
export const formatMoney = (money: Money | null | undefined): string => {
  if (!money || money.value == null) return EMPTY
  const symbol = CCY_SYMBOL[money.currency] ?? `${money.currency} `
  const num = Math.abs(money.value).toLocaleString('en-US', {
    minimumFractionDigits: MONEY_DECIMALS,
    maximumFractionDigits: MONEY_DECIMALS,
  })
  return money.value < 0 ? `(${symbol}${num})` : `${symbol}${num}`
}

/** The API has no MONEY type: a NUM column is right-aligned and, at runtime, upgraded to money
 *  presentation whenever its value carries a currency; a plain number (no currency envelope) is
 *  the common case for a NUM field, not an edge case, so it has to resolve to itself here rather
 *  than to nothing. A NUM field also arrives as a bare numeric string on occasion — Prometheus's
 *  `marketValue` and an un-unwrapped Enfusion envelope both do this (see row.adapter.ts's header
 *  comment) — so a string is coerced rather than treated the same as genuine non-numeric text. */
export const numericValueOf = (value: unknown): number | null => {
  if (isMoney(value)) return value.value
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

export const moneyValueFormatter = (params: ValueFormatterParams): string => {
  if (isMoney(params.value)) return formatMoney(params.value)
  const value = numericValueOf(params.value)
  // No thousands separator: the catalog does not distinguish an id/count NUM field (where a comma
  // would be wrong) from a genuine quantity, so the plain value renders untouched rather than
  // guessing which kind this one is.
  return value == null ? EMPTY : String(value)
}

export const moneyComparator = (a: unknown, b: unknown): number => {
  const av = numericValueOf(a) ?? Number.NEGATIVE_INFINITY
  const bv = numericValueOf(b) ?? Number.NEGATIVE_INFINITY
  return av - bv
}

/** `YYYY-MM-DD` with no time part — a calendar date rather than an instant. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Parse a cell value into a local Date.
 *
 * Date-only strings need care: `new Date('2026-04-30')` is specified to parse as **UTC** midnight,
 * while `getDate()` and friends read back in **local** time. West of UTC that combination renders a
 * position dated the 30th as the 29th. These are business dates carrying no time zone, so they are
 * built from their own parts and stay the same calendar date for every viewer.
 *
 * Values that do carry a time (`2026-04-30T12:00:00`) already parse as local time, so they are left
 * to the built-in parser.
 */
export const parseCellDate = (value: unknown): Date | null => {
  if (value == null || value === '') return null
  if (typeof value === 'string') {
    const parts = DATE_ONLY.exec(value)
    if (parts) {
      return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
    }
  }
  const date = new Date(value as string)
  return isNaN(date.getTime()) ? null : date
}

export const formatDate = (value: unknown): string => {
  if (value == null || value === '') return EMPTY
  const date = parseCellDate(value)
  if (!date) return String(value)
  const mm = `${date.getMonth() + 1}`.padStart(2, '0')
  const dd = `${date.getDate()}`.padStart(2, '0')
  return `${mm}/${dd}/${date.getFullYear()}`
}

export const formatDateTime = (value: unknown): string => {
  if (value == null || value === '') return EMPTY
  const date = parseCellDate(value)
  if (!date) return String(value)
  return `${formatDate(value)} ${date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })}`
}

export const formatBoolean = (value: unknown): string =>
  value == null ? EMPTY : value ? 'Yes' : 'No'

/** A field the catalog types as plain text/number but whose value is actually an object (a nested
 *  posData shape the catalog does not describe) has no known display rule yet — render its JSON
 *  rather than ag-Grid's default `[object Object]` stringification. */
export const formatCell = (value: unknown): string => {
  if (value == null || value === '') return EMPTY
  if (isMoney(value)) return formatMoney(value)
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const dimmedDash = (icon?: string): string =>
  icon
    ? `<span style="color:#9ca3af;opacity:.5"><i class="mdi ${icon}"></i></span>`
    : `<span style="color:#9ca3af;opacity:.5">${EMPTY}</span>`

// matched/unmatched use the same green/red as Helios 1.0's .text-success/.text-danger
// (clearwater.css), not the design prototype's own #028d53/#b70406 (docs/Positions 2.0.dc.html).
const STATUS_STYLE: Record<string, { label: string; color: string }> = {
  matched: { label: 'MATCHED', color: '#378645' },
  unmatched: { label: 'UNMATCHED', color: '#db1f30' },
  ignored: { label: 'IGNORED', color: '#52607d' },
}

export const statusCellRenderer = (params: ICellRendererParams): string => {
  const style = STATUS_STYLE[String(params.value)]
  if (!style) return ''
  return `<span style="font-weight:700;letter-spacing:.3px;color:${style.color}">${style.label}</span>`
}

const ORIGIN_COLOR: Record<RowOrigin, string> = {
  api: '#7d5fff',
  cust: '#217dfe',
  trad: '#e48727',
  cw: '#0047d1',
}

export const sourceCellRenderer = (params: ICellRendererParams): string => {
  const value = String(params.value ?? '')
  if (!value) return ''
  const color = params.data
    ? ORIGIN_COLOR[params.data.origin as RowOrigin]
    : '#52607d'
  return `<span style="font-weight:700;letter-spacing:.3px;color:${color}">${value.toUpperCase()}</span>`
}

/**
 * Build a per-target match-count renderer coloured by the target source. Zero renders as a dimmed
 * dash; a non-zero count renders as a link icon + count, which cross-filters the other grid.
 */
export const makeMatchCountRenderer =
  (color: string) =>
  (params: ICellRendererParams): string => {
    const count = Number(params.value ?? 0)
    // Same link / link-off symbol as the Clearwater match-count column in Transactions 1.0.
    if (!count) return dimmedDash('mdi-link-off')
    return (
      `<span style="display:inline-flex;align-items:center;gap:4px;cursor:pointer;color:${color}">` +
      `<i class="mdi mdi-link"></i><span style="font-weight:700">${count}</span></span>`
    )
  }

export const matchTargetColor = (target: keyof typeof TARGET_META): string =>
  TARGET_META[target].color

export const commentsCellRenderer = (params: ICellRendererParams): string => {
  const count = Number(params.value ?? 0)
  const color = count > 0 ? '#1162e7' : '#9ca3af'
  const opacity = count > 0 ? '1' : '.4'
  return (
    `<span style="display:inline-flex;align-items:center;gap:4px;color:${color};opacity:${opacity}">` +
    `<i class="mdi mdi-comment-outline"></i><span style="font-weight:700">${count}</span></span>`
  )
}
